import { CoreTypes, type ConnectorEnd, type Id, type ObjectRecord } from '@vp/shared-types';
import { boxBounds, localToWorld, worldToLocal, type CanvasDocument, type Rect, type TxContext, type Vec2 } from '@vp/core';

/** Connector routing with obstacle avoidance (§5.4). */

export type Dir = 'up' | 'down' | 'left' | 'right' | null;

export interface Endpoint {
  p: Vec2;
  dir: Dir;
  objectId?: Id;
  /** World bounds of the attached shape. */
  box?: Rect;
}

const MARGIN = 16;
const STUB = 20;

const SIDES: { u: number; v: number; dir: Exclude<Dir, null> }[] = [
  { u: 0.5, v: 0, dir: 'up' },
  { u: 1, v: 0.5, dir: 'right' },
  { u: 0.5, v: 1, dir: 'down' },
  { u: 0, v: 0.5, dir: 'left' },
];

function rotateDir(dir: Exclude<Dir, null>, rotation: number): Exclude<Dir, null> {
  const order: Exclude<Dir, null>[] = ['up', 'right', 'down', 'left'];
  const steps = Math.round(rotation / (Math.PI / 2));
  return order[(((order.indexOf(dir) + steps) % 4) + 4) % 4];
}

/** Resolve a connector end to a world point and exit direction. */
export function resolveEnd(doc: CanvasDocument, end: ConnectorEnd, toward: Vec2): Endpoint {
  const target = end.objectId ? doc.get(end.objectId) : undefined;
  if (!target) return { p: end.point, dir: null };
  if (isLineLike(target)) {
    // attached to another connector/line: a point along its current path
    const pts = worldPath(target);
    return { p: pts.length >= 4 ? pointAt(pts, end.t ?? 0.5) : end.point, dir: null, objectId: target.id };
  }
  if (end.anchor) {
    const p = localToWorld(target, { x: end.anchor.x * target.w, y: end.anchor.y * target.h });
    // direction from the nearest side
    const a = end.anchor;
    const dists = [a.y, 1 - a.x, 1 - a.y, a.x];
    const side = SIDES[dists.indexOf(Math.min(...dists))];
    return { p, dir: rotateDir(side.dir, target.rotation), objectId: target.id, box: boxBounds(target) };
  }
  // automatic: side midpoint closest to the other end
  let best: Endpoint | null = null;
  let bestD = Infinity;
  for (const s of SIDES) {
    const p = localToWorld(target, { x: s.u * target.w, y: s.v * target.h });
    const d = Math.hypot(p.x - toward.x, p.y - toward.y);
    if (d < bestD) {
      bestD = d;
      best = { p, dir: rotateDir(s.dir, target.rotation), objectId: target.id };
    }
  }
  best!.box = boxBounds(target);
  return best!;
}

const DIR_VEC: Record<Exclude<Dir, null>, Vec2> = { up: { x: 0, y: -1 }, down: { x: 0, y: 1 }, left: { x: -1, y: 0 }, right: { x: 1, y: 0 } };

/**
 * Smooth route: a cubic curve leaving each end along its side's direction (like DSkhema links);
 * free ends aim at the other end.
 */
export function curveBetween(a: Endpoint, b: Endpoint, unit = 1): Vec2[] {
  const dist = Math.hypot(b.p.x - a.p.x, b.p.y - a.p.y);
  const reach = Math.max(18 * unit, Math.min(dist * 0.45, 200 * unit));
  const toward = (from: Vec2, to: Vec2): Vec2 => {
    const d = Math.hypot(to.x - from.x, to.y - from.y) || 1;
    return { x: (to.x - from.x) / d, y: (to.y - from.y) / d };
  };
  const da = a.dir ? DIR_VEC[a.dir] : toward(a.p, b.p);
  const db = b.dir ? DIR_VEC[b.dir] : toward(b.p, a.p);
  const c1 = { x: a.p.x + da.x * reach, y: a.p.y + da.y * reach };
  const c2 = { x: b.p.x + db.x * reach, y: b.p.y + db.y * reach };
  const out: Vec2[] = [];
  const steps = 24;
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const u = 1 - t;
    out.push({
      x: u * u * u * a.p.x + 3 * u * u * t * c1.x + 3 * u * t * t * c2.x + t * t * t * b.p.x,
      y: u * u * u * a.p.y + 3 * u * u * t * c1.y + 3 * u * t * t * c2.y + t * t * t * b.p.y,
    });
  }
  return out;
}

/** Center of an end (for computing the opposite automatic anchor). */
export function endCenter(doc: CanvasDocument, end: ConnectorEnd): Vec2 {
  const t = end.objectId ? doc.get(end.objectId) : undefined;
  if (!t) return end.point;
  if (isLineLike(t)) return resolveEnd(doc, end, end.point).p;
  if (end.anchor) return localToWorld(t, { x: end.anchor.x * t.w, y: end.anchor.y * t.h });
  return { x: t.x + t.w / 2, y: t.y + t.h / 2 };
}

export function isLineLike(o: ObjectRecord): boolean {
  return o.type === CoreTypes.connector || o.type === CoreTypes.line;
}

/** World-space polyline of a connector or line. */
export function worldPath(o: ObjectRecord): number[] {
  const local = (o.type === CoreTypes.line ? o.props['points'] : o.props['path']) as number[] | undefined;
  if (!Array.isArray(local)) return [];
  const out: number[] = [];
  for (let i = 0; i + 1 < local.length; i += 2) {
    const p = localToWorld(o, { x: local[i], y: local[i + 1] });
    out.push(p.x, p.y);
  }
  return out;
}

/** Point at normalized length `t` along a polyline. */
export function pointAt(pts: number[], t: number): Vec2 {
  let total = 0;
  for (let i = 2; i < pts.length; i += 2) total += Math.hypot(pts[i] - pts[i - 2], pts[i + 1] - pts[i - 1]);
  let target = Math.max(0, Math.min(1, t)) * total;
  for (let i = 2; i < pts.length; i += 2) {
    const seg = Math.hypot(pts[i] - pts[i - 2], pts[i + 1] - pts[i - 1]);
    if (target <= seg && seg > 0) {
      const k = target / seg;
      return { x: pts[i - 2] + (pts[i] - pts[i - 2]) * k, y: pts[i - 1] + (pts[i + 1] - pts[i - 1]) * k };
    }
    target -= seg;
  }
  return { x: pts[pts.length - 2], y: pts[pts.length - 1] };
}

/** Closest point on a polyline and its normalized length parameter. */
export function closestOnPath(pts: number[], p: Vec2): { point: Vec2; t: number; dist: number } {
  let total = 0;
  for (let i = 2; i < pts.length; i += 2) total += Math.hypot(pts[i] - pts[i - 2], pts[i + 1] - pts[i - 1]);
  let best = { point: { x: pts[0], y: pts[1] }, t: 0, dist: Infinity };
  let acc = 0;
  for (let i = 2; i < pts.length; i += 2) {
    const ax = pts[i - 2], ay = pts[i - 1], bx = pts[i], by = pts[i + 1];
    const dx = bx - ax, dy = by - ay;
    const len2 = dx * dx + dy * dy;
    const k = len2 > 0 ? Math.max(0, Math.min(1, ((p.x - ax) * dx + (p.y - ay) * dy) / len2)) : 0;
    const q = { x: ax + dx * k, y: ay + dy * k };
    const d = Math.hypot(q.x - p.x, q.y - p.y);
    const seg = Math.sqrt(len2);
    if (d < best.dist) best = { point: q, t: total > 0 ? (acc + seg * k) / total : 0, dist: d };
    acc += seg;
  }
  return best;
}

/** Outline of an object in local coordinates (for gluing to its frame). */
function localOutline(o: ObjectRecord): number[] {
  const { w, h } = o;
  if (o.type === CoreTypes.ellipse) {
    const pts: number[] = [];
    for (let i = 0; i < 64; i++) {
      const a = (i / 64) * Math.PI * 2;
      pts.push(w / 2 + (Math.cos(a) * w) / 2, h / 2 + (Math.sin(a) * h) / 2);
    }
    return pts;
  }
  if (o.type === CoreTypes.triangle) return [w / 2, 0, w, h, 0, h];
  if (o.type === CoreTypes.diamond) return [w / 2, 0, w, h / 2, w / 2, h, 0, h / 2];
  return [0, 0, w, 0, w, h, 0, h];
}

/**
 * Binding for a connector end dropped at world point `p` over `target` (§5.4):
 * near the center → automatic side; otherwise glued to the nearest point of the
 * object's frame (moves and rotates with the object); connectors/lines → a point on their path.
 */
export function bindTo(target: ObjectRecord, p: Vec2): ConnectorEnd {
  if (isLineLike(target)) {
    const c = closestOnPath(worldPath(target), p);
    return { objectId: target.id, t: c.t, point: c.point };
  }
  const l = worldToLocal(target, p);
  const inner = Math.abs(l.x - target.w / 2) < target.w * 0.18 && Math.abs(l.y - target.h / 2) < target.h * 0.18;
  if (inner || target.w <= 0 || target.h <= 0) return { objectId: target.id, point: p };
  const outline = localOutline(target);
  const c = closestOnPath([...outline, outline[0], outline[1]], l);
  const anchor = { x: c.point.x / target.w, y: c.point.y / target.h };
  return { objectId: target.id, anchor, point: localToWorld(target, c.point) };
}

/** Ids of connectors that (transitively) attach to `id` — binding to them would create a loop. */
export function dependentConnectors(doc: CanvasDocument, id: Id): Set<Id> {
  const out = new Set<Id>([id]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const o of doc.allObjects()) {
      if (o.type !== CoreTypes.connector || out.has(o.id)) continue;
      const s = o.props['start'] as ConnectorEnd | undefined;
      const e = o.props['end'] as ConnectorEnd | undefined;
      if ((s?.objectId && out.has(s.objectId)) || (e?.objectId && out.has(e.objectId))) {
        out.add(o.id);
        grew = true;
      }
    }
  }
  return out;
}

function step(p: Vec2, dir: Dir, d: number): Vec2 {
  switch (dir) {
    case 'up': return { x: p.x, y: p.y - d };
    case 'down': return { x: p.x, y: p.y + d };
    case 'left': return { x: p.x - d, y: p.y };
    case 'right': return { x: p.x + d, y: p.y };
    default: return p;
  }
}

function insideAny(x: number, y: number, rects: Rect[]): boolean {
  for (const r of rects) if (x > r.x && x < r.x + r.w && y > r.y && y < r.y + r.h) return true;
  return false;
}

function segBlocked(ax: number, ay: number, bx: number, by: number, rects: Rect[]): boolean {
  for (const r of rects) {
    if (ax === bx) {
      if (ax > r.x && ax < r.x + r.w && Math.max(ay, by) > r.y && Math.min(ay, by) < r.y + r.h) return true;
    } else if (ay > r.y && ay < r.y + r.h && Math.max(ax, bx) > r.x && Math.min(ax, bx) < r.x + r.w) return true;
  }
  return false;
}

/** Remove collinear / duplicate points. */
export function simplify(pts: Vec2[]): Vec2[] {
  const out: Vec2[] = [];
  for (const p of pts) {
    const n = out.length;
    if (n && out[n - 1].x === p.x && out[n - 1].y === p.y) continue;
    if (n >= 2) {
      const a = out[n - 2];
      const b = out[n - 1];
      if ((a.x === b.x && b.x === p.x) || (a.y === b.y && b.y === p.y)) {
        out[n - 1] = p;
        continue;
      }
    }
    out.push(p);
  }
  return out;
}

const OPPOSITE_DIR: Record<Exclude<Dir, null>, Exclude<Dir, null>> = { up: 'down', down: 'up', left: 'right', right: 'left' };

function segDir(p: Vec2, q: Vec2): Exclude<Dir, null> | null {
  if (p.x === q.x && p.y === q.y) return null;
  if (p.y === q.y) return q.x > p.x ? 'right' : 'left';
  if (p.x === q.x) return q.y > p.y ? 'down' : 'up';
  return null;
}

function polyLength(pts: Vec2[]): number {
  let len = 0;
  for (let i = 1; i < pts.length; i++) len += Math.abs(pts[i].x - pts[i - 1].x) + Math.abs(pts[i].y - pts[i - 1].y);
  return len;
}

/** Distance from `p` along `dir` to the edge of `box` (0 when already outside). */
function exitDistance(p: Vec2, dir: Exclude<Dir, null>, box: Rect): number {
  const d = dir === 'right' ? box.x + box.w - p.x : dir === 'left' ? p.x - box.x : dir === 'down' ? box.y + box.h - p.y : p.y - box.y;
  return Math.max(0, d);
}

/**
 * Straight, L and Z routes without stubs that leave and enter along the ends' directions, never
 * double back and keep off the shapes (the ends' own shapes are only checked without clearance).
 * Fewest bends first, then shortest.
 */
function simpleRoute(a: Endpoint, b: Endpoint, obstacles: Rect[], margin: number): Vec2[] | null {
  const own = [a.box, b.box].filter((r): r is Rect => !!r);
  const isOwn = (r: Rect) => own.some((o) => o.x === r.x && o.y === r.y && o.w === r.w && o.h === r.h);
  const near = obstacles.filter((r) => !isOwn(r)).map((r) => ({ x: r.x - margin, y: r.y - margin, w: r.w + margin * 2, h: r.h + margin * 2 }));
  const enterDir = b.dir ? OPPOSITE_DIR[b.dir] : null;
  const mx = (a.p.x + b.p.x) / 2;
  const my = (a.p.y + b.p.y) / 2;
  const candidates: Vec2[][] = [
    [a.p, b.p],
    [a.p, { x: b.p.x, y: a.p.y }, b.p],
    [a.p, { x: a.p.x, y: b.p.y }, b.p],
    [a.p, { x: mx, y: a.p.y }, { x: mx, y: b.p.y }, b.p],
    [a.p, { x: a.p.x, y: my }, { x: b.p.x, y: my }, b.p],
  ];
  let best: Vec2[] | null = null;
  for (const raw of candidates) {
    const dirs: Exclude<Dir, null>[] = [];
    let ok = true;
    for (let i = 1; i < raw.length && ok; i++) {
      if (raw[i].x === raw[i - 1].x && raw[i].y === raw[i - 1].y) continue;
      const d = segDir(raw[i - 1], raw[i]);
      if (!d || (dirs.length && OPPOSITE_DIR[d] === dirs[dirs.length - 1])) ok = false;
      else dirs.push(d);
      if (ok && (segBlocked(raw[i - 1].x, raw[i - 1].y, raw[i].x, raw[i].y, near) || segBlocked(raw[i - 1].x, raw[i - 1].y, raw[i].x, raw[i].y, own))) ok = false;
    }
    if (!ok || !dirs.length) continue;
    if (a.dir && dirs[0] !== a.dir) continue;
    if (enterDir && dirs[dirs.length - 1] !== enterDir) continue;
    const pts = simplify(raw);
    if (!best || pts.length < best.length || (pts.length === best.length && polyLength(pts) < polyLength(best))) best = pts;
  }
  return best;
}

/**
 * Orthogonal route between two endpoints avoiding obstacle rectangles.
 * A* over a sparse grid built from obstacle edges, with a bend penalty.
 */
export function routeOrthogonal(a: Endpoint, b: Endpoint, obstacles: Rect[], unit = 1): Vec2[] {
  // clearances scale with the connector (unit = 1 for a 2 px line)
  const margin = MARGIN * unit;
  const stub = STUB * unit;
  const simple = simpleRoute(a, b, obstacles, margin / 2);
  const searched = searchRoute(a, b, obstacles, margin, stub);
  // the search keeps clearances, but when the stubs cross (shapes close together) it can only loop
  if (simple && (!searched || simple.length <= searched.length || polyLength(searched) > polyLength(simple) + stub * 4)) return simple;
  if (searched) return searched;
  const sa = step(a.p, a.dir, a.dir ? stub : 0);
  const sb = step(b.p, b.dir, b.dir ? stub : 0);
  const mid = a.dir === 'up' || a.dir === 'down' ? { x: sa.x, y: sb.y } : { x: sb.x, y: sa.y };
  return simplify([a.p, sa, mid, sb, b.p]);
}

function searchRoute(a: Endpoint, b: Endpoint, obstacles: Rect[], margin: number, stub: number): Vec2[] | null {
  const inflated = obstacles.map((r) => ({ x: r.x - margin, y: r.y - margin, w: r.w + margin * 2, h: r.h + margin * 2 }));
  // a stub must clear its own shape's clearance (anchors inside the bounds: triangle sides, ellipse points)
  const reach = (e: Endpoint) => (e.dir ? Math.max(stub, (e.box ? exitDistance(e.p, e.dir, e.box) : 0) + margin) : 0);
  const sa = step(a.p, a.dir, reach(a));
  const sb = step(b.p, b.dir, reach(b));
  // obstacles containing a stub point are ignored (endpoints may sit inside overlapping shapes)
  const rects = inflated.filter((r) => !insideAny(sa.x, sa.y, [r]) && !insideAny(sb.x, sb.y, [r]));

  const xsSet = new Set<number>([sa.x, sb.x, (sa.x + sb.x) / 2]);
  const ysSet = new Set<number>([sa.y, sb.y, (sa.y + sb.y) / 2]);
  for (const r of rects) {
    xsSet.add(r.x);
    xsSet.add(r.x + r.w);
    ysSet.add(r.y);
    ysSet.add(r.y + r.h);
  }
  const xs = [...xsSet].sort((p, q) => p - q);
  const ys = [...ysSet].sort((p, q) => p - q);
  const W = xs.length;
  const H = ys.length;
  if (W * H > 40000) return null;
  const ix0 = xs.indexOf(sa.x), iy0 = ys.indexOf(sa.y);
  const ix1 = xs.indexOf(sb.x), iy1 = ys.indexOf(sb.y);

  // state = (node, dir 0..3) ; dirs: 0 +x, 1 -x, 2 +y, 3 -y
  const N = W * H * 4;
  const g = new Float64Array(N).fill(Infinity);
  const prev = new Int32Array(N).fill(-1);
  const BEND = 40;
  const heap: [number, number][] = [];
  const push = (f: number, s: number) => {
    heap.push([f, s]);
    let i = heap.length - 1;
    while (i > 0) {
      const pi = (i - 1) >> 1;
      if (heap[pi][0] <= heap[i][0]) break;
      [heap[pi], heap[i]] = [heap[i], heap[pi]];
      i = pi;
    }
  };
  const pop = (): [number, number] | undefined => {
    if (!heap.length) return undefined;
    const top = heap[0];
    const last = heap.pop()!;
    if (heap.length) {
      heap[0] = last;
      let i = 0;
      for (;;) {
        const l = i * 2 + 1, r = l + 1;
        let m = i;
        if (l < heap.length && heap[l][0] < heap[m][0]) m = l;
        if (r < heap.length && heap[r][0] < heap[m][0]) m = r;
        if (m === i) break;
        [heap[m], heap[i]] = [heap[i], heap[m]];
        i = m;
      }
    }
    return top;
  };
  const hfun = (ix: number, iy: number) => Math.abs(xs[ix] - sb.x) + Math.abs(ys[iy] - sb.y);
  const startDir = a.dir === 'right' ? 0 : a.dir === 'left' ? 1 : a.dir === 'down' ? 2 : a.dir === 'up' ? 3 : -1;
  for (let d = 0; d < 4; d++) {
    if (startDir >= 0 && d !== startDir) continue;
    const s = ((iy0 * W + ix0) << 2) | d;
    g[s] = 0;
    push(hfun(ix0, iy0), s);
  }
  const endDirWanted = b.dir === 'right' ? 1 : b.dir === 'left' ? 0 : b.dir === 'down' ? 3 : b.dir === 'up' ? 2 : -1;
  let found = -1;
  let iterations = 0;
  while (heap.length && iterations++ < 200000) {
    const [, s] = pop()!;
    const node = s >> 2;
    const dir = s & 3;
    const ix = node % W;
    const iy = (node / W) | 0;
    if (ix === ix1 && iy === iy1) {
      found = s;
      break;
    }
    const moves: [number, number, number][] = [
      [ix + 1, iy, 0],
      [ix - 1, iy, 1],
      [ix, iy + 1, 2],
      [ix, iy - 1, 3],
    ];
    for (const [nx, ny, nd] of moves) {
      if (nx < 0 || ny < 0 || nx >= W || ny >= H) continue;
      if ((dir ^ 1) === nd && dir < 2 === nd < 2) continue; // no reversal
      const x0 = xs[ix], y0 = ys[iy], x1 = xs[nx], y1 = ys[ny];
      if (insideAny(x1, y1, rects) || segBlocked(x0, y0, x1, y1, rects)) continue;
      let cost = g[s] + Math.abs(x1 - x0) + Math.abs(y1 - y0) + (nd !== dir ? BEND : 0);
      if (nx === ix1 && ny === iy1 && endDirWanted >= 0 && nd !== endDirWanted) {
        // arriving backwards would overshoot the stub and double back into the arrowhead
        if ((nd ^ 1) === endDirWanted && nd < 2 === endDirWanted < 2) continue;
        cost += BEND;
      }
      const ns = ((ny * W + nx) << 2) | nd;
      if (cost < g[ns]) {
        g[ns] = cost;
        prev[ns] = s;
        push(cost + hfun(nx, ny), ns);
      }
    }
  }
  if (found < 0) return null;
  const path: Vec2[] = [];
  for (let s = found; s >= 0; s = prev[s]) {
    const node = s >> 2;
    path.push({ x: xs[node % W], y: ys[(node / W) | 0] });
  }
  path.reverse();
  return simplify([a.p, ...path, b.p]);
}

/** Route between two connector ends; returns world points and resolved end points. */
export function computeRoute(
  doc: CanvasDocument,
  start: ConnectorEnd,
  end: ConnectorEnd,
  route: string,
  obstaclesIn: (r: Rect) => Id[],
  skipId?: Id,
  unit = 1,
): { pts: Vec2[]; a: Vec2; b: Vec2 } {
  const a = resolveEnd(doc, start, endCenter(doc, end));
  const b = resolveEnd(doc, end, a.p);
  if (route === 'straight') return { pts: [a.p, b.p], a: a.p, b: b.p };
  if (route === 'curved') return { pts: curveBetween(a, b, unit), a: a.p, b: b.p };
  const region = {
    x: Math.min(a.p.x, b.p.x) - 200 * unit,
    y: Math.min(a.p.y, b.p.y) - 200 * unit,
    w: Math.abs(a.p.x - b.p.x) + 400 * unit,
    h: Math.abs(a.p.y - b.p.y) + 400 * unit,
  };
  const obstacles: Rect[] = [];
  for (const id of obstaclesIn(region)) {
    if (id === skipId) continue;
    const o = doc.get(id);
    if (!o || o.type === CoreTypes.connector || o.type === CoreTypes.line || o.type === CoreTypes.frame || o.type === CoreTypes.group) continue;
    // freehand strokes and open pen paths are drawings, not shapes to walk around
    if (o.type === CoreTypes.pen || (o.type === CoreTypes.path && o.props['closed'] !== true)) continue;
    obstacles.push(boxBounds(o));
    if (obstacles.length >= 40) break;
  }
  return { pts: routeOrthogonal(a, b, obstacles, unit), a: a.p, b: b.p };
}

export function pointsBox(pts: Vec2[]): { x: number; y: number; w: number; h: number; local: number[] } {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const p of pts) {
    x0 = Math.min(x0, p.x);
    y0 = Math.min(y0, p.y);
    x1 = Math.max(x1, p.x);
    y1 = Math.max(y1, p.y);
  }
  const local: number[] = [];
  for (const p of pts) local.push(p.x - x0, p.y - y0);
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0, local };
}

/** Recompute a connector's geometry (box + local path) from its ends. */
export function rerouteConnector(tx: TxContext, conn: ObjectRecord, obstaclesIn: (r: Rect) => Id[]): void {
  const start = conn.props['start'] as ConnectorEnd;
  const end = conn.props['end'] as ConnectorEnd;
  if (!start || !end) return;
  const unit = Math.max(0.05, (conn.style.stroke?.width ?? 2) / 2);
  const r = computeRoute(tx.document, start, end, (conn.props['route'] as string) ?? 'orthogonal', obstaclesIn, conn.id, unit);
  const box = pointsBox(r.pts);
  tx.update(conn.id, {
    x: box.x,
    y: box.y,
    w: box.w,
    h: box.h,
    rotation: 0,
    props: { ...conn.props, start: { ...start, point: r.a }, end: { ...end, point: r.b }, path: box.local },
  });
}
/** Connectors attached to any of `ids`. */
export function connectorsAttachedTo(doc: CanvasDocument, ids: Set<Id>): ObjectRecord[] {
  const out: ObjectRecord[] = [];
  for (const o of doc.allObjects()) {
    if (o.type !== CoreTypes.connector) continue;
    const s = o.props['start'] as ConnectorEnd | undefined;
    const e = o.props['end'] as ConnectorEnd | undefined;
    if ((s?.objectId && ids.has(s.objectId)) || (e?.objectId && ids.has(e.objectId))) out.push(o);
  }
  return out;
}

export function rectsOverlap(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

